import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { PopupStyles } from "./CustomerDetailsStyles";
import Theme from "../../../../utils/Theme";
import React from "react";

const vehicleStatusLabel = (vehicleStatusId) => {
  if (vehicleStatusId === 1) return "New Vehicle";
  if (vehicleStatusId === 2) return "Trade Vehicle";
  return "N/A";
};

const vehicleRowKey = (vehicle) => {
  if (vehicle?.vehicle_id != null) return `id:${vehicle.vehicle_id}`;
  const vin = (vehicle?.vin || "").trim();
  if (vin) return `vin:${vin}`;
  return `ymm:${vehicle?.make_year ?? ""}|${(vehicle?.make ?? "").toLowerCase()}|${(vehicle?.model ?? "").toLowerCase()}`;
};

const matchesYmm = (vehicle, vtr) => {
  if (!vehicle || !vtr) return false;
  return (
    String(vehicle.make_year ?? vehicle.year ?? "") ===
      String(vtr.year ?? "") &&
    (vehicle.make ?? "").toLowerCase() === (vtr.make ?? "").toLowerCase() &&
    (vehicle.model ?? "").toLowerCase() === (vtr.model ?? "").toLowerCase()
  );
};

const buildVehicleRows = (insuranceVehicles, standaloneVehicles, vtr) => {
  const rows = [];
  const seen = new Set();

  const addRow = (vehicle) => {
    const key = vehicleRowKey(vehicle);
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(vehicle);
  };

  [...(insuranceVehicles || []), ...(standaloneVehicles || [])].forEach(addRow);

  if (vtr && (vtr.year || vtr.make || vtr.model)) {
    const alreadyListed = rows.some((row) => matchesYmm(row, vtr));
    if (!alreadyListed) {
      addRow({
        vin: null,
        make_year: vtr.year,
        make: vtr.make,
        model: vtr.model,
        vehicle_status_id: vtr.vehicle_status_id,
      });
    }
  }

  return rows;
};

const InsuranceService = ({ insuranceDetails, vehicleTransactionResponse }) => {
  const insurance = insuranceDetails?.object;
  const vtr = vehicleTransactionResponse;

  const vehicleData = Array.isArray(insurance)
    ? insurance.filter((item) => item?.vehicle_id)
    : [];
  const quoteQuestions = Array.isArray(insurance)
    ? insurance.filter((item) => item?.question)
    : [];

  const insuranceVehicles = insuranceDetails?.insurance_id
    ? insurance?.vehicle || []
    : [];
  const vehicleRows = buildVehicleRows(
    insuranceVehicles,
    vehicleData,
    vtr,
  );

  const hasPolicy = Boolean(insuranceDetails?.insurance_id);
  const hasContent =
    hasPolicy || vehicleRows.length > 0 || quoteQuestions.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        alignItems: "center",
        overflow: "scroll",
        gap: 1,
      }}
    >
      {hasContent ? (
        <>
          {hasPolicy && (
            <Table className="border mb-6">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f2f2f2" }}>
                  <TableCell className="font-bold p-6">Carrier Name</TableCell>
                  <TableCell className="font-bold p-6">Yearly Premium</TableCell>
                  <TableCell className="font-bold p-6">Policy Number</TableCell>
                  <TableCell className="p-6" />
                  <TableCell className="p-6" />
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className="p-6">
                    {insurance?.carrier_name || "N/A"}
                  </TableCell>
                  <TableCell className="p-6">
                    {insurance?.total_premium
                      ? `$ ${insurance.total_premium}`
                      : "N/A"}
                  </TableCell>
                  <TableCell className="p-6">
                    {insurance?.policy_number || "N/A"}
                  </TableCell>
                  <TableCell className="p-6" />
                  <TableCell className="p-6" />
                </TableRow>
              </TableBody>
            </Table>
          )}

          {vehicleRows.length > 0 && (
            <Table className="border mb-6">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                  <TableCell className="font-bold p-6">VIN</TableCell>
                  <TableCell className="font-bold p-6">New / Trade</TableCell>
                  <TableCell className="font-bold p-6">Year</TableCell>
                  <TableCell className="font-bold p-6">Make</TableCell>
                  <TableCell className="font-bold p-6">Model</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicleRows.map((vehicle, vIndex) => (
                  <TableRow key={vehicleRowKey(vehicle) || vIndex}>
                    <TableCell className="p-6">
                      {vehicle?.vin || "N/A"}
                    </TableCell>
                    <TableCell className="p-6">
                      {vehicleStatusLabel(vehicle?.vehicle_status_id)}
                    </TableCell>
                    <TableCell className="p-6">
                      {vehicle?.make_year || "N/A"}
                    </TableCell>
                    <TableCell className="p-6">
                      {vehicle?.make || "N/A"}
                    </TableCell>
                    <TableCell className="p-6">
                      {vehicle?.model || "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {(hasPolicy
            ? insurance?.quoteQuestions
            : quoteQuestions)?.length > 0 && (
            <>
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                  alignSelf: "flex-start",
                }}
              >
                Quote Details:
              </span>
              <Table>
                <TableBody>
                  {(hasPolicy ? insurance.quoteQuestions : quoteQuestions).map(
                    (question, index) => (
                      <TableRow sx={PopupStyles.rowStyle} key={index}>
                        <TableCell
                          className="p-6"
                          colSpan={3}
                          sx={PopupStyles.childCell}
                        >
                          <div style={{ display: "flex", gap: "4px" }}>
                            <span>{question.question}</span>
                            <span style={{ color: Theme.palette.primary.main }}>
                              {question.response || "N/A"}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </>
          )}

          {!hasPolicy && <Divider sx={{ mb: 2 }} />}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>No Data Available</Typography>
        </Box>
      )}
    </Box>
  );
};

export default InsuranceService;
